import { datadogRum } from "@datadog/browser-rum";
import { useQueryClient } from "@tanstack/react-query";
import { unwrapConnection } from "@utils/utils";
import { useCallback, useMemo, useState } from "react";

import {
  ApiReportGoalChartTypeChoices,
  ReportGoalType,
  ReportTargetType,
  useGetReassessmentReportGoalsQuery,
  useUpdateGoalProgressSummaryMutation,
  useUpdateTargetProgressSummaryMutation,
} from "@api/graphql/types-and-hooks";
import { ProcessOutcome, useCapturePipeline } from "@components/ChartCapture/hooks/useCapturePipeline";
import { CaptureJob, CaptureResult } from "@components/ChartCapture/types";
import { isDataBearingFailure } from "@components/ChartCapture/utils/isDataBearingFailure";
import { ApproveAllAttestation } from "@pages/ClinicalReportDetails/ReportPage/Components/ApproveAllAttestationDialog";

interface UnapprovedTarget {
  target: ReportTargetType;
  goalId: string;
}

interface ApproveAllResult {
  successes: number;
  failures: number;
}

/**
 * Bulk-approves every still-unreviewed goal and target on a Reassessment
 * report in one shot. Pairs with the BCBA-only "Approve all unreviewed"
 * menu entry in the goals-sidebar header (gated on the `approve-all-goals`
 * LaunchDarkly flag).
 *
 * Uses the same query key as `useReassessmentApprovalProgress`
 * (`["get-reassessment-goals", reportId]`) so React Query dedupes — no extra
 * fetch when the progress indicator is mounted.
 *
 * Reads from an *unfiltered* query rather than the sidebar's possibly-filtered
 * `goalList`, so applying status/reviewed filters in the UI doesn't quietly
 * shrink the set of items "Approve all" actually approves.
 */
export const useApproveAllUnreviewed = (reportId: string | undefined) => {
  const queryClient = useQueryClient();
  const goalsQuery = useGetReassessmentReportGoalsQuery(
    { filter: { reportId: reportId ?? "" } },
    {
      queryKey: ["get-reassessment-goals", reportId],
      enabled: !!reportId,
    },
  );

  const goalList: ReportGoalType[] = useMemo(
    () => (unwrapConnection(goalsQuery.data?.getReassessmentReportGoals) as ReportGoalType[]) ?? [],
    [goalsQuery.data],
  );

  const { unapprovedGoals, unapprovedTargets } = useMemo(() => {
    const goals = goalList.filter((g) => !g.summaryReviewed);
    const targets: UnapprovedTarget[] = goalList.flatMap((g) =>
      ((unwrapConnection(g.reporttargetSet) as ReportTargetType[]) ?? [])
        .filter((t) => !t.summaryReviewed && !t.excludeFromReport)
        .map((target) => ({ target, goalId: g.id })),
    );
    return { unapprovedGoals: goals, unapprovedTargets: targets };
  }, [goalList]);

  const { mutateAsync: updateGoal } = useUpdateGoalProgressSummaryMutation({});
  const { mutateAsync: updateTarget } = useUpdateTargetProgressSummaryMutation({});
  const [isApplying, setIsApplying] = useState(false);

  // Per-item step of the pipeline: honor the invariant, then persist. A
  // data-bearing chart that failed to capture surfaces as an error (not
  // approved); no-data items are approved with an empty image.
  const processResult = useCallback(
    async (job: CaptureJob, result: CaptureResult): Promise<ProcessOutcome> => {
      if (isDataBearingFailure(result)) {
        return { ok: false, error: "Chart couldn't be captured" };
      }
      try {
        if (job.kind === "goal") {
          const res = await updateGoal({
            input: {
              goalId: job.goal.id,
              isApproved: true,
              chartType:
                (job.goal.chartType as ApiReportGoalChartTypeChoices) ?? ApiReportGoalChartTypeChoices.Progress,
              chartImageUri: result.imageUri ?? "",
            },
          });
          return res.updateGoalProgressSummary?.status ? { ok: true } : { ok: false, error: "Failed to save" };
        }
        const res = await updateTarget({
          input: { targetId: job.target.id, isApproved: true, chartImageUri: result.imageUri ?? "" },
        });
        return res.updateTargetProgressSummary?.status ? { ok: true } : { ok: false, error: "Failed to save" };
      } catch (err) {
        console.error("Failed to save approval", err);
        return { ok: false, error: "Failed to save" };
      }
    },
    [updateGoal, updateTarget],
  );

  const { jobs, items, run, handleItemStart, handleItemCaptured } = useCapturePipeline(processResult);

  const approveAll = async (attestation: ApproveAllAttestation): Promise<ApproveAllResult> => {
    setIsApplying(true);

    try {
      // TODO(audit-backend): this Datadog event is the *interim* attestation
      // record. Datadog gives us an immutable, timestamped, user-identified
      // log (App.tsx:41 attaches email/sub/name/orgId/orgName via
      // datadogRum.setUser), which is queryable for audit but originates
      // client-side. For a payor- and licensure-defensible audit trail the
      // same attestation must be persisted server-side alongside the
      // approval — open a backend ticket to add a mutation like
      // `recordApprovalAttestation(reportId, text, attestedAt)` that writes
      // an immutable row, then call it from here alongside this event.
      // Inside the try so a Datadog throw still resets isApplying.
      datadogRum.addAction("clinical-attestation-approve-all", {
        reportId,
        attestationText: attestation.text,
        attestedAt: attestation.attestedAt,
        goalIds: unapprovedGoals.map((g) => g.id),
        targetIds: unapprovedTargets.map(({ target }) => target.id),
        goalCount: unapprovedGoals.length,
        targetCount: unapprovedTargets.length,
      });

      // Report-relevant items go through the visible pipeline (chart render →
      // per-item mutation). Excluded goals never appear in the report, so they
      // are approved without an image and outside the progress indicator.
      const jobsToRun: CaptureJob[] = [
        ...unapprovedGoals.filter((g) => !g.excludeFromReport).map((goal): CaptureJob => ({ kind: "goal", goal })),
        ...unapprovedTargets.map(({ target, goalId }): CaptureJob => ({ kind: "target", goalId, target })),
      ];

      const finalItems = await run(jobsToRun);
      const successes = finalItems.filter((it) => it.status === "done").length;
      const failures = finalItems.filter((it) => it.status === "error").length;

      // Refresh every surface that renders approval state: list query, open
      // goal panel, open target panel. Caught locally so a refresh blip can't
      // swallow the success toast (mutations already persisted server-side).
      try {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ["get-reassessment-goals"] }),
          queryClient.invalidateQueries({ queryKey: ["ReassessmentShortTermGoalPanel"] }),
          queryClient.invalidateQueries({ queryKey: ["getReportTargets"] }),
        ]);
      } catch (err) {
        console.error("Failed to refresh after bulk goal/target approval", err);
      }

      return { successes, failures };
    } finally {
      setIsApplying(false);
    }
  };

  return {
    approveAll,
    isApplying,
    unapprovedGoalCount: unapprovedGoals.length,
    unapprovedTargetCount: unapprovedTargets.length,
    captureJobs: jobs,
    captureItems: items,
    onItemStart: handleItemStart,
    onItemCaptured: handleItemCaptured,
  };
};
